'use client'

import * as React from "react"
import {isValidElement, useState} from "react"
import {Slot, Slottable} from "@radix-ui/react-slot"
import {cva, type VariantProps} from "class-variance-authority"

import {cn} from "@/lib/utils"
import {Spinner} from "@/components/Spinner";

const buttonVariants = cva(
  "cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 rounded-md gap-1 px-2 has-[>svg]:px-1",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
                  children,
                  className,
                  variant,
                  size,
                  onClick,
                  asChild = false,
                  loading = false,
                  ...props
                }: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
  asChild?: boolean
  loading?: boolean
}) {
  const [internalLoading, setInternalLoading] = useState(false)
  const _onClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const promise: any = onClick?.(e)
    if (promise instanceof Promise) {
      setInternalLoading(true)
      promise.finally(() => setInternalLoading(false))
    }
  }
  const Comp = asChild ? Slot : "button"
  const child = asChild ? [
    <Slottable key={'child1'}>{children}</Slottable>,
    (loading || internalLoading) && <Slottable key={'child2'}><Spinner/></Slottable>,
  ] : <React.Fragment key={'child3'}>
    {(loading || internalLoading) && <>{isValidElement(children) ? <Spinner/> : children}</>}
    {(!loading && !internalLoading) && <>{(children) }</>}
  </React.Fragment>
  return (
    <Comp
      type="button"
      data-slot="button"
      className={cn(buttonVariants({variant, size, className}))}
      onClick={_onClick}
      {...props}
    >
      {child}
    </Comp>
  )
}

export {Button, buttonVariants}
